import type { CSSProperties } from 'react';
import styles from './Avatar.module.css';

// orbit Avatar는 member 객체에서 name/initials/bg/fg를 직접 받았다.
// 새 모델은 email/name 기반으로 deterministic하게 색을 만든다.
// 이렇게 하면 backend에서 따로 색을 내려주지 않아도 안정적인 아바타가 된다.

export type AvatarSize = 'sm' | 'md' | 'lg';

interface AvatarProps {
  email: string;
  name?: string | null;
  size?: AvatarSize | number;
  ring?: boolean;
}

const SIZE_PX: Record<AvatarSize, number> = {
  sm: 24,
  md: 32,
  lg: 40,
};

// 안정적인 hash → 팔레트 인덱스. 같은 email은 항상 같은 색.
const PALETTE: Array<{ bg: string; fg: string }> = [
  { bg: '#EDE6FF', fg: '#4A1FB0' },
  { bg: '#E3F2FF', fg: '#0F5D8A' },
  { bg: '#E1F7E5', fg: '#1F5A2C' },
  { bg: '#FFE4F4', fg: '#A1147A' },
  { bg: '#FFEFD4', fg: '#7A5310' },
  { bg: '#FFE4DD', fg: '#A12D14' },
  { bg: '#F4F4F4', fg: '#5C5C5C' },
];

function hashCode(input: string): number {
  let h = 0;
  for (let i = 0; i < input.length; i += 1) {
    h = (h << 5) - h + input.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

function initialsFor(email: string, name?: string | null): string {
  const source = (name && name.trim()) || email;
  // 이메일이면 @ 앞부분만, 이름이면 첫 글자 + 공백 뒤 첫 글자
  const cleaned = source.includes('@') ? source.split('@')[0] : source;
  const parts = cleaned.split(/[.\s_-]+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export function Avatar({ email, name, size = 'md', ring = false }: AvatarProps) {
  const sizePx = typeof size === 'number' ? size : SIZE_PX[size];
  const palette = PALETTE[hashCode(email.toLowerCase()) % PALETTE.length];
  const inline: CSSProperties = {
    width: sizePx,
    height: sizePx,
    background: palette.bg,
    color: palette.fg,
    fontSize: Math.round(sizePx * 0.36),
  };
  const classes = [styles.avatar, ring ? styles.ring : ''].filter(Boolean).join(' ');
  return (
    <div className={classes} style={inline} title={name ?? email}>
      {initialsFor(email, name)}
    </div>
  );
}
